import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUserSession } from "@/hooks/use-user-session";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const withTimeout = async <T,>(
  operation: () => PromiseLike<T>,
  ms = 10000
): Promise<T> => {
  return new Promise<T>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error("Request timed out. Please check your connection and try again."));
    }, ms);

    operation().then(
      (result) => {
        clearTimeout(timeoutId);
        resolve(result);
      },
      (error) => {
        clearTimeout(timeoutId);
        reject(error);
      }
    );
  });
};

export const ProfileCompletionWizard = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: session } = useUserSession();
  const userId = session?.userId ?? null;
  const userType = session?.userType ?? null;

  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const initialisedRef = useRef(false);
  const autoCompletedRef = useRef(false);

  // Single parent-students existence check, shared between mount-skip and post-submit.
  const { data: hasExistingStudents } = useQuery({
    queryKey: ["wizard-parent-students", userId],
    enabled: !!userId && userType === "parent",
    queryFn: async () => {
      const { data, error } = await supabase
        .from("student_profiles")
        .select("id")
        .eq("parent_user_id", userId!)
        .limit(1);
      if (error) throw error;
      return (data?.length || 0) > 0;
    },
    staleTime: 60 * 1000,
  });

  // Seed local form state once from the cached session profile.
  useEffect(() => {
    if (initialisedRef.current) return;
    if (!session?.userId) return;
    setFullName(session.fullName || "");
    setPhone(session.phone || "");
    initialisedRef.current = true;
  }, [session?.userId, session?.fullName, session?.phone]);

  const completeWizard = async () => {
    if (isLoading) return;
    if (!userId) {
      toast.error("No user found");
      return;
    }
    setIsLoading(true);
    try {
      const { error } = await withTimeout(
        () =>
          supabase
            .from("profiles")
            .update({ profile_completed: true })
            .eq("id", userId)
      );

      if (error) {
        console.error("Error marking profile as completed:", error);
        throw error;
      }

      await queryClient.invalidateQueries({ queryKey: ["user-session"] });

      toast.success("Profile setup complete!");
      // Full page load so ProtectedRoute re-fetches fresh data
      window.location.href = "/assessment/dashboard";
    } catch (error: any) {
      console.error("Error in completeWizard:", error);
      toast.error(error.message || "Failed to complete setup");
      setIsLoading(false);
    }
  };

  // Auto-skip the wizard when the profile is already populated.
  useEffect(() => {
    if (autoCompletedRef.current) return;
    if (!session?.userId) return;
    // Wait for the parent-students check if we're a parent
    if (userType === "parent" && hasExistingStudents === undefined) return;

    const hasName = !!session.fullName;
    const hasPhone = !!session.phone;
    if (!hasName || !hasPhone) return;

    if (userType === "parent" && hasExistingStudents) {
      autoCompletedRef.current = true;
      completeWizard();
    } else if (userType === "parent" && !hasExistingStudents) {
      setStep(2);
    } else if (userType !== "parent") {
      autoCompletedRef.current = true;
      completeWizard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.userId, session?.fullName, session?.phone, userType, hasExistingStudents]);

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim()) {
      toast.error("Please enter your phone number");
      return;
    }
    if (!userId) {
      toast.error("No user found");
      return;
    }
    setIsLoading(true);

    try {
      const { error: profileError } = await withTimeout(
        () =>
          supabase
            .from("profiles")
            .update({
              full_name: fullName,
              phone: phone,
            })
            .eq("id", userId),
        5000
      );

      if (profileError) {
        console.error("Profile update error:", profileError);
        throw new Error(`Failed to update profile: ${profileError.message}`);
      }

      await queryClient.invalidateQueries({ queryKey: ["user-session"] });

      if (userType === "parent") {
        if (hasExistingStudents) {
          await completeWizard();
        } else {
          setStep(2);
        }
      } else {
        await completeWizard();
      }
    } catch (error: any) {
      console.error("❌ Error in handleProfileSubmit:", error);
      toast.error(error.message || "Failed to update profile. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (step === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl">
          <CardHeader className="text-center space-y-2">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-primary" />
            </div>
            <CardTitle>Complete Your Profile</CardTitle>
            <CardDescription>
              Let's add some additional information to get you started
            </CardDescription>
            <div className="flex gap-2 justify-center pt-2">
              <div className="h-2 w-2 rounded-full bg-primary" />
              {userType === "parent" && <div className="h-2 w-2 rounded-full bg-muted" />}
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleProfileSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name *</Label>
                <Input
                  id="fullName"
                  placeholder="Enter your full name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="Enter your phone number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                />
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "Saving..." : userType === "parent" ? "Next" : "Complete Setup"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === 2 && userType === "parent") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl">
          <CardHeader className="text-center space-y-2">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-primary" />
            </div>
            <CardTitle>Almost Done!</CardTitle>
            <CardDescription>
              Your profile has been set up successfully.
            </CardDescription>
            <div className="flex gap-2 justify-center pt-2">
              <div className="h-2 w-2 rounded-full bg-primary/30" />
              <div className="h-2 w-2 rounded-full bg-primary" />
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-muted/50 border rounded-lg p-4 text-center">
              <p className="text-sm text-muted-foreground">
                Your center manager will assign your child's profile to your account.
                Once assigned, you'll be able to view their assessments and results.
              </p>
            </div>

            <Button
              onClick={completeWizard}
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? "Completing..." : "Continue to Dashboard"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};
